import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout/Layout';
import { toast } from 'react-toastify';
import Seo from '../../components/Seo';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const SupportTicketCreate = () => {
    const { t } = useTranslation();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({ category_id: '', subject: '', message: '' });
    const [attachment, setAttachment] = useState(null);
    const [attachPreview, setAttachPreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!authLoading && !user) navigate('/login');
    }, [authLoading, user, navigate]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await fetch(`${API_URL}tickets.php?action=categories`);
                const data = await res.json();
                setCategories(Array.isArray(data) ? data : []);
                if (Array.isArray(data) && data.length > 0) {
                    setFormData(prev => ({ ...prev, category_id: data[0].id }));
                }
            } catch (error) {
                toast.error(t('support.failedLoadCategories') || 'Failed to load categories');
            }
        };
        if (user) fetchCategories();
    }, [user, t]);

    const handleAttachChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAttachment(file);
        setAttachPreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        let attachmentUrl = null;
        if (attachment) {
            const fd = new FormData();
            fd.append('image', attachment);
            try {
                const res = await fetch(`${API_URL}upload.php`, { method: 'POST', body: fd });
                const result = await res.json();
                if (result.success) attachmentUrl = result.url;
            } catch {
                toast.error(t('support.uploadFailed') || 'Upload failed');
            }
        }

        try {
            const response = await fetch(`${API_URL}tickets.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, user_id: user.id, attachment_url: attachmentUrl })
            });
            const result = await response.json();
            if (result.success) {
                toast.success(t('support.submittedSuccess') || 'Ticket submitted successfully');
                setTimeout(() => navigate('/support'), 2000);
            } else {
                toast.error(result.error || t('support.failedSubmit') || 'Failed to submit ticket');
            }
        } catch {
            toast.error(t('common.connectionError') || 'Connection error');
        }
        setIsSubmitting(false);
    };

    if (authLoading || !user) return null;

    return (
        <Layout hideScrollbar>
            <Seo title={t('support.createTicket')} />
            <main className="page_content pt-150 pb-120">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-6">
                            <div className="support-card p-5">
                                <div className="d-flex align-items-center gap-3 mb-5">
                                    <button onClick={() => navigate('/support')} className="btn-back-link">
                                        <i className="fas fa-arrow-left"></i>
                                    </button>
                                    <h2 className="text-white mb-0">{t('support.createTicket')}</h2>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-4">
                                        <label className="text-white-50 small mb-2">{t('support.issueCategory')}</label>
                                        <select className="form-control-modern" value={formData.category_id} onChange={(e) => setFormData({...formData, category_id: e.target.value})} required>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-4">
                                        <label className="text-white-50 small mb-2">{t('support.subject')}</label>
                                        <input type="text" className="form-control-modern" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required placeholder={t('support.subjectPlaceholder') || "Enter subject"} />
                                    </div>
                                    <div className="mb-4">
                                        <label className="text-white-50 small mb-2">{t('support.detailedMessage')}</label>
                                        <textarea className="form-control-modern" rows="6" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required placeholder={t('support.messagePlaceholder') || "Describe your issue in detail..."}></textarea>
                                    </div>
                                    <div className="mb-5">
                                        <label className="text-white-50 small mb-2">{t('support.attachScreenshot')}</label>
                                        {attachPreview && (
                                            <div className="mb-3 position-relative d-inline-block">
                                                <img src={attachPreview} alt="Preview" style={{maxWidth:'100%',maxHeight:'200px',borderRadius:'12px',border:'1px solid rgba(255,255,255,0.1)'}} />
                                                <button type="button" className="btn-remove-attach" onClick={() => { setAttachment(null); setAttachPreview(null); if(fileInputRef.current) fileInputRef.current.value = ''; }}>
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        )}
                                        <label className="btn-attach w-100">
                                            <i className="fas fa-camera me-2"></i>
                                            {attachment ? attachment.name : t('support.chooseImage')}
                                            <input type="file" accept="image/*" onChange={handleAttachChange} ref={fileInputRef} style={{ display: 'none' }} />
                                        </label>
                                    </div>
                                    <button type="submit" className="btn-submit w-100" disabled={isSubmitting}>
                                        {isSubmitting ? t('support.sendingRequest') : t('support.submitRequest')}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <style>{`
                .support-card { background: rgba(255,255,255,0.03); border-radius: 24px; border: 1px solid rgba(255,255,255,0.07); }
                .form-control-modern { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #fff; width: 100%; padding: 15px 20px; border-radius: 12px; }
                .btn-submit { background: #6c5ce7; color: #fff; border: none; padding: 16px; border-radius: 12px; font-weight: 700; cursor: pointer; transition: 0.3s; }
                .btn-submit:hover:not(:disabled) { background: #5a4ad1; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(108, 92, 231, 0.3); }
                .btn-attach { display: block; text-align: center; background: rgba(255,255,255,0.04); border: 1px dashed rgba(255,255,255,0.2); color: rgba(255,255,255,0.6); padding: 15px; border-radius: 12px; cursor: pointer; transition: 0.2s; }
                .btn-attach:hover { border-color: #6c5ce7; color: #6c5ce7; }
                .btn-back-link { width: 45px; height: 45px; border-radius: 50%; background: rgba(255,255,255,0.05); color: #fff; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; }
                .btn-back-link:hover { background: #6c5ce7; }
                .btn-remove-attach { position: absolute; top: -10px; right: -10px; width: 28px; height: 28px; border-radius: 50%; background: #ff4757; color: #fff; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(0,0,0,0.3); }
            `}</style>
        </Layout>
    );
};

export default SupportTicketCreate;
